// src/pages/OnboardingPage.jsx — Fullscreen Role-Based First-Time Onboarding
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Shield, Compass, FileCheck, ArrowRight, CheckCircle2,
  Users, TrendingUp, Car, Clock, MessageSquare, Briefcase
} from "lucide-react";
import PolicyAcceptanceModal from "../components/onboarding/PolicyAcceptanceModal";
import api from "../services/api";
import "../components/onboarding/onboarding.css";

const ROLE_METRICS = {
  Agent: {
    badge: "Sales Agent Workspace",
    headline: "Welcome to your Platinum CRM Sales Command Center",
    description: "Your authorized workspace is optimized to help you connect with high-intent vehicle buyers, manage follow-ups, and track commissions with full transparency.",
    features: [
      {
        icon: <TrendingUp size={20} />,
        name: "Lead Pipeline & Inquiries",
        desc: "Manage prospective automotive buyers from initial WhatsApp contact through price quotation, negotiation, and sale closure."
      },
      {
        icon: <Clock size={20} />,
        name: "Daily Follow-up Workbench",
        desc: "Prioritize hot buyer callbacks, record truthful customer remarks, and never miss an scheduled sales appointment."
      },
      {
        icon: <Car size={20} />,
        name: "Japanese Stock Inventory",
        desc: "Direct access to hundreds of verified Japanese auction vehicles with FOB/CIF pricing and customer share links."
      },
      {
        icon: <MessageSquare size={20} />,
        name: "Team Chat & PlatinumBot",
        desc: "Collaborate with colleagues and tap into AI assistant insights for immediate lead status and vehicle matching."
      }
    ]
  },
  Manager: {
    badge: "Department Management Portal",
    headline: "Welcome to your Sales Team Operations Portal",
    description: "Oversee sales velocity, govern lead assignments, monitor agent follow-up quality, and ensure team conversion benchmarks are met.",
    features: [
      {
        icon: <Users size={20} />,
        name: "Team Governance & Routing",
        desc: "Equitably distribute fresh incoming buyer inquiries and reallocate stalled leads to prevent response bottlenecks."
      },
      {
        icon: <TrendingUp size={20} />,
        name: "Sales Velocity & Leaderboards",
        desc: "Track agent conversion rates, target achievements, and monitor month-to-date sales figures in real time."
      },
      {
        icon: <Clock size={20} />,
        name: "Attendance & Break Oversight",
        desc: "Live visibility into staff duty status, break durations, and administrative break timer resets."
      },
      {
        icon: <Briefcase size={20} />,
        name: "Deal Approvals & Portfolio",
        desc: "Review negotiated vehicle proposals, manage customer portfolios, and verify finalized sales contracts."
      }
    ]
  },
  Admin: {
    badge: "Executive Administration Center",
    headline: "Welcome to the Platinum CRM Enterprise Control Center",
    description: "You have executive authority over all organizational assets, employees, brand configurations, payroll calculations, and security policies.",
    features: [
      {
        icon: <Shield size={20} />,
        name: "Security & Staff Management",
        desc: "Manage employee credentials, role permissions, active device sessions, and audit compliance logs."
      },
      {
        icon: <Briefcase size={20} />,
        name: "Automated Payroll & Commissions",
        desc: "Verify closed vehicle deals, calculate agent commissions, manage deductions, and approve monthly payouts."
      },
      {
        icon: <Car size={20} />,
        name: "Global Stock Management",
        desc: "Import Japanese vehicle inventory, govern website listings, and assign multi-brand business units."
      },
      {
        icon: <FileCheck size={20} />,
        name: "Policy Compliance Audits",
        desc: "Monitor company policy acknowledgments, active policy versioning, and legal acceptance records."
      }
    ]
  }
};

export default function OnboardingPage() {
  const { user } = useAuth();

  // Current onboarding stage: 'welcome' | 'policies' | 'post-policy'
  const [stage, setStage] = useState("welcome");

  const userRole = user?.role || "Agent";
  const roleConfig = ROLE_METRICS[userRole] || ROLE_METRICS.Agent;

  // If user has already accepted policies, jump to post-policy choice
  useEffect(() => {
    api.get("/onboarding/status")
      .then((res) => {
        if (res.data?.onboardingCompleted && !res.data?.needsUpdate) {
          setStage("post-policy");
        }
      })
      .catch(() => {});
  }, []);

  const handleStartTour = () => {
    window.location.href = "/dashboard?tour=true";
  };

  const handleEnterDirectly = () => {
    window.location.href = "/dashboard";
  };

  return (
    <div className="onboarding-screen-wrap">
      {/* ── Stage 1: Welcome Overview ── */}
      {stage === "welcome" && (
        <div className="onboarding-card-shell">
          <div className="onboarding-head">
            <div className="onboarding-badge-role">
              <Shield size={14} /> {roleConfig.badge}
            </div>
            <h2 className="onboarding-title">
              Welcome, {user?.name || "Team Member"}
            </h2>
            <p className="onboarding-subtitle">
              {roleConfig.description}
            </p>
          </div>

          {/* Role-Specific Capabilities */}
          <div className="onboarding-features-grid">
            {roleConfig.features.map((feat, idx) => (
              <div key={idx} className="onboarding-feature-item">
                <div className="onboarding-feature-icon">
                  {feat.icon}
                </div>
                <div className="onboarding-feature-name">{feat.name}</div>
                <p className="onboarding-feature-desc">{feat.desc}</p>
              </div>
            ))}
          </div>

          {/* Action Navigation */}
          <div className="onboarding-actions-row">
            <div style={{ marginRight: "auto", fontSize: "12.5px", color: "var(--muted)" }}>
              Mandatory compliance review required before accessing CRM workspace
            </div>

            <button
              type="button"
              className="onboarding-btn-primary"
              onClick={() => setStage("policies")}
            >
              <FileCheck size={16} /> Review &amp; Accept Company Policies <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ── Stage 2: Mandatory Company Policies Acceptance (Executive Document Reader) ── */}
      {stage === "policies" && (
        <PolicyAcceptanceModal
          user={user}
          onSuccess={() => setStage("post-policy")}
        />
      )}

      {/* ── Stage 2.5: Post-Acceptance Choice: Guided Tour vs Direct Access ── */}
      {stage === "post-policy" && (
        <div className="onboarding-card-shell" style={{ maxWidth: 580, textAlign: "center" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(48, 209, 88, 0.15)", border: "2px solid rgba(48, 209, 88, 0.4)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", color: "#30d158" }}>
            <CheckCircle2 size={36} />
          </div>

          <h2 className="onboarding-title" style={{ fontSize: "24px" }}>
            Compliance Verified &amp; Accepted
          </h2>
          <p className="onboarding-subtitle" style={{ maxWidth: 460, margin: "10px auto 28px" }}>
            Your account is now fully compliant and authorized. Would you like a brief 60-second interactive tour of your {userRole} workspace?
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <button
              type="button"
              className="onboarding-btn-primary"
              style={{ width: "100%", justifyContent: "center" }}
              onClick={handleStartTour}
            >
              <Compass size={18} /> Start Interactive Guided Tour <ArrowRight size={18} />
            </button>

            <button
              type="button"
              className="onboarding-btn-secondary"
              style={{ width: "100%", justifyContent: "center" }}
              onClick={handleEnterDirectly}
            >
              Enter Platinum CRM Directly
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
